// src/components/steps/ColorStep.js
import React from 'react';
import {
  VStack,
  Text,
  Box,
  Button,
  SimpleGrid,
  HStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { Check } from 'lucide-react';

const colorOptions = [
  { 
    value: 'pink', 
    label: 'ピンク系',
    color: 'pink.400',
    samples: ['pink.300', 'pink.400', 'pink.500']
  },
  { 
    value: 'red', 
    label: 'レッド系',
    color: 'red.400',
    samples: ['red.300', 'red.400', 'red.500']
  },
  { 
    value: 'purple', 
    label: 'パープル系',
    color: 'purple.400',
    samples: ['purple.300', 'purple.400', 'purple.500']
  },
  { 
    value: 'blue', 
    label: 'ブルー系',
    color: 'blue.400',
    samples: ['blue.300', 'blue.400', 'blue.500']
  },
  { 
    value: 'green', 
    label: 'グリーン系',
    color: 'green.400',
    samples: ['green.300', 'green.400', 'green.500']
  },
  { 
    value: 'brown', 
    label: 'ブラウン系',
    color: 'orange.700',
    samples: ['orange.600', 'orange.700', 'orange.800']
  },
  { 
    value: 'beige', 
    label: 'ベージュ系',
    color: 'orange.200',
    samples: ['orange.100', 'orange.200', 'orange.300']
  },
  { 
    value: 'black', 
    label: 'ブラック系',
    color: 'gray.700',
    samples: ['gray.600', 'gray.700', 'gray.800']
  },
  { 
    value: 'metallic', 
    label: 'シルバー・ゴールド系',
    color: 'gray.400',
    samples: ['gray.300', 'gray.400', 'yellow.300']
  }
];

const ColorStep = ({ formData, onChange }) => {
  const handleColorToggle = (color) => {
    const currentColors = formData.colors || [];
    const newColors = currentColors.includes(color)
      ? currentColors.filter(c => c !== color)
      : [...currentColors, color];
    
    onChange({
      target: { name: 'colors', value: newColors }
    });
  };

  return (
    <VStack spacing={{ base: 6, md: 8 }} align="stretch">
      <Box>
        <Text
          fontSize={{ base: "xl", md: "2xl" }}
          fontWeight="bold"
          textAlign="center"
          bgGradient="linear(to-r, pink.400, blue.400)"
          bgClip="text"
        >
          お好みのカラーを選んでください
        </Text>
        <Text 
          fontSize={{ base: "md", md: "md" }}
          color="gray.600" 
          fontWeight="normal" 
          textAlign="center"
          mt={2}
        >
          複数選択可能です
        </Text>
      </Box>

      <SimpleGrid 
        columns={{ base: 1, sm: 2, md: 3 }} 
        spacing={{ base: 3, md: 4 }}
      >
        {colorOptions.map((option) => (
          <Button
            key={option.value}
            onClick={() => handleColorToggle(option.value)}
            height="auto"
            p={{ base: 3, md: 4 }}
            bg="white"
            color="gray.700"
            _hover={{ 
              transform: 'translateY(-2px)',
              boxShadow: 'md',
              transition: 'all 0.2s'
            }}
            border="2px"
            borderColor={
              formData.colors?.includes(option.value)
                ? option.color
                : 'gray.200'
            }
            borderRadius="xl"
            position="relative"
          >
            {/* チェックマーク */}
            {formData.colors?.includes(option.value) && (
              <Box
                position="absolute"
                top="-2px"
                right="-2px"
                bg={option.color}
                color="white"
                borderRadius="full"
                p={1}
              >
                <Check size={16} />
              </Box>
            )}

            <VStack spacing={3} align="stretch" width="100%">
              <Text 
                fontSize={{ base: "md", md: "lg" }}
                fontWeight="bold"
              >
                {option.label}
              </Text>
              
              {/* カラーサンプル */}
              <HStack spacing={2} justify="center">
                {option.samples.map((color, index) => (
                  <Box
                    key={index}
                    w={{ base: "20px", md: "24px" }}
                    h={{ base: "20px", md: "24px" }}
                    borderRadius="full"
                    bg={color}
                    border="2px solid white"
                    boxShadow="sm"
                  />
                ))}
              </HStack>
            </VStack>
          </Button>
        ))}
      </SimpleGrid>

      {/* 選択中のカラー表示 */}
      {formData.colors?.length > 0 && (
        <Box mt={{ base: 3, md: 4 }}>
          <Text 
            fontSize={{ base: "sm", md: "sm" }}
            color="gray.600" 
            mb={2}
          >
            選択中のカラー:
          </Text>
          <Wrap spacing={2}>
            {formData.colors.map((color) => {
              const option = colorOptions.find(opt => opt.value === color);
              return (
                <WrapItem key={color}>
                  <Box
                    px={3}
                    py={1}
                    bg={option.color}
                    color="white"
                    borderRadius="full"
                    fontSize={{ base: "xs", md: "sm" }}
                  >
                    {option.label}
                  </Box>
                </WrapItem>
              );
            })}
          </Wrap>
        </Box>
      )}

      <Box
        mt={{ base: 4, md: 6 }}
        p={{ base: 4, md: 6 }}
        bg="pink.50"
        borderRadius="xl"
        borderLeft="4px"
        borderColor="pink.400"
      >
        <Text 
          color="pink.600"
          fontSize={{ base: "sm", md: "md" }}
        >
          お好みのカラーに近いランドセルを見つけるお手伝いをします。
          複数のカラーを選択すると、より幅広い選択肢からお選びいただけます。
        </Text>
      </Box>
    </VStack>
  );
};

export default ColorStep;